import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, milestoneApi, WEB_ORIGIN } from '../../api/client';
import type { CarouselEvent, Member } from '../../types';
import { format } from 'date-fns';
import { parseEventDate } from '../../utils/eventDate';

function toDateInput(iso: string | null): string {
  if (!iso) return '';
  try {
    return format(parseEventDate(iso), 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

export function MilestoneSlideshowSetup() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [members, setMembers] = useState<Member[]>([]);
  const [personId, setPersonId] = useState('');
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [titleWasEdited, setTitleWasEdited] = useState(false);

  const [existingEvents, setExistingEvents] = useState<CarouselEvent[]>([]);
  const [convenienceEventId, setConvenienceEventId] = useState('');

  const [milestoneId, setMilestoneId] = useState<string | null>(id ?? null);
  const [isPublic, setIsPublic] = useState(false);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState('');

  const [preview, setPreview] = useState<{ events: CarouselEvent[]; totalPhotos: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [loadingInitial, setLoadingInitial] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [error, setError] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    api.get('/account/members').then(({ data }) => setMembers(Array.isArray(data) ? data : []));
    api.get('/events').then(({ data }) => setExistingEvents(data.events ?? []));

    if (isEdit && id) {
      milestoneApi.get(id).then(m => {
        setPersonId(m.person?.id ?? '');
        setTitle(m.title);
        setStart(toDateInput(m.startDate));
        setEnd(toDateInput(m.endDate));
        setIsPublic(m.isPublic);
        if (m.isPublic && m.publicToken) {
          setPublicUrl(`${WEB_ORIGIN}/share/milestone/${m.publicToken}`);
        }
        if (m.publicExpiresAt) setExpiresAt(toDateInput(m.publicExpiresAt));
        setPreview({ events: m.events, totalPhotos: m.totalPhotos });
      }).finally(() => setLoadingInitial(false));
    }
  }, [id, isEdit]);

  // Debounced live preview — pure read, no side effects, works before anything is saved.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!personId) { setPreview(null); return; }
    debounceRef.current = setTimeout(() => {
      setPreviewLoading(true);
      milestoneApi.preview(personId, start || undefined, end || undefined)
        .then(data => setPreview(data))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [personId, start, end]);

  const personName = members.find(m => m.user.id === personId)?.user.name ?? '';

  function handleConvenienceEvent(eventId: string) {
    setConvenienceEventId(eventId);
    const event = existingEvents.find(e => e.id === eventId);
    if (!event) return;
    if (event.eventDate) setEnd(toDateInput(event.eventDate));
    if (!titleWasEdited) {
      setTitle(personName ? `${personName}'s Journey to ${event.title}` : `Journey to ${event.title}`);
    }
  }

  // Pure create-or-update — never navigates itself, so it's safe to call from
  // any action (Save, Start Slideshow, Share) without triggering an unwanted
  // extra route change as a side effect of a completely different button.
  const ensureSaved = useCallback(async (): Promise<string> => {
    if (milestoneId) {
      await milestoneApi.update(milestoneId, { title, start: start || undefined, end: end || undefined });
      return milestoneId;
    }
    const created = await milestoneApi.create({ personId, title, start: start || undefined, end: end || undefined });
    setMilestoneId(created.id);
    return created.id;
  }, [milestoneId, personId, title, start, end]);

  async function handleSave() {
    if (!personId || !title.trim()) { setError('Person and title are required'); return; }
    setError('');
    setSaving(true);
    const wasNew = !milestoneId;
    try {
      const savedId = await ensureSaved();
      if (wasNew) navigate(`/events/milestones/${savedId}/edit`, { replace: true });
      setSavedMessage('Saved — find it any time in My Milestones.');
      setTimeout(() => setSavedMessage(''), 4000);
    } catch {
      setError('Failed to save milestone');
    } finally {
      setSaving(false);
    }
  }

  async function handleStartSlideshow() {
    if (!personId || !title.trim()) { setError('Person and title are required'); return; }
    setError('');
    setSaving(true);
    try {
      const savedId = await ensureSaved();
      navigate(`/carousel?mode=milestone&milestoneId=${savedId}`);
    } catch {
      setError('Failed to save milestone');
      setSaving(false);
    }
  }

  async function handleShare() {
    setError('');
    setSaving(true);
    try {
      const savedId = await ensureSaved();
      const { publicUrl: url } = await milestoneApi.publish(savedId, expiresAt || undefined);
      setPublicUrl(url);
      setIsPublic(true);
    } catch {
      setError('Failed to create share link');
    } finally {
      setSaving(false);
    }
  }

  async function handleUnshare() {
    if (!milestoneId) return;
    await milestoneApi.unpublish(milestoneId);
    setPublicUrl(null);
    setIsPublic(false);
  }

  const thenPhoto = preview?.events[0]?.photos[0] ?? null;
  const lastEvent = preview?.events[preview.events.length - 1];
  const nowPhoto = lastEvent?.photos[lastEvent.photos.length - 1] ?? null;

  if (loadingInitial) return <div className="max-w-2xl"><p className="text-slate-500">Loading…</p></div>;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">✨</span>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          {isEdit ? 'Edit Milestone' : 'Milestone Slideshow'}
        </h1>
      </div>
      <p className="text-slate-500 text-sm mb-6">Tell one person's story, one photo at a time.</p>

      <div className="space-y-5 bg-white rounded-2xl p-6 shadow-sm border border-indigo-50">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Person</label>
          <select
            data-testid="milestone-person-select"
            value={personId}
            onChange={e => setPersonId(e.target.value)}
            disabled={isEdit}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-500"
          >
            <option value="">Select a person…</option>
            {members.filter(m => !m.petType).map(m => (
              <option key={m.user.id} value={m.user.id}>{m.user.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
          <input
            type="text"
            data-testid="milestone-title-input"
            value={title}
            onChange={e => { setTitle(e.target.value); setTitleWasEdited(true); }}
            placeholder="e.g. Sarah's Journey to Her Wedding Day"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start date</label>
            <input
              type="date"
              data-testid="milestone-start-input"
              value={start}
              onChange={e => setStart(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-slate-400 mt-1">Blank uses their earliest tagged photo</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End date</label>
            <input
              type="date"
              data-testid="milestone-end-input"
              value={end}
              onChange={e => setEnd(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-slate-400 mt-1">Blank uses today</p>
          </div>
        </div>

        {!isEdit && existingEvents.length > 0 && (
          <div className="border border-dashed border-indigo-200 rounded-xl p-4 bg-indigo-50/40">
            <span className="block text-xs font-semibold uppercase tracking-wide text-indigo-600 mb-2">
              Optional convenience
            </span>
            <label className="block text-sm font-medium text-slate-700 mb-1">Base end date on an existing event</label>
            <select
              value={convenienceEventId}
              onChange={e => handleConvenienceEvent(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— None —</option>
              {existingEvents.map(e => (
                <option key={e.id} value={e.id}>
                  {e.title}{e.eventDate ? ` · ${format(parseEventDate(e.eventDate), 'MMM d, yyyy')}` : ''}
                </option>
              ))}
            </select>
            {convenienceEventId && (
              <p className="text-xs text-indigo-600 mt-2 pl-3 border-l-2 border-indigo-200">
                ↳ Prefilled the end date and suggested a title — both stay fully editable.
              </p>
            )}
          </div>
        )}

        <div data-testid="milestone-preview" className="rounded-xl p-4 bg-gradient-to-br from-indigo-50 to-indigo-50/40 border border-indigo-100">
          {!personId ? (
            <p className="text-sm text-slate-500">Select a person to see a preview.</p>
          ) : previewLoading ? (
            <p className="text-sm text-slate-500">Searching tagged photos…</p>
          ) : !preview || preview.totalPhotos === 0 ? (
            <p className="text-sm text-slate-500">
              No tagged photos found{personName ? ` for ${personName}` : ''} in this range — try widening the dates, or double-check they're face-tagged in some photos.
            </p>
          ) : (
            <>
              <p data-testid="milestone-preview-count" className="text-base font-semibold text-slate-800">
                {preview.totalPhotos} photo{preview.totalPhotos === 1 ? '' : 's'} across {preview.events.length} event{preview.events.length === 1 ? '' : 's'} found
              </p>
              {thenPhoto && nowPhoto && (
                <div data-testid="milestone-then-now" className="flex items-center gap-3 mt-3">
                  <div className="flex-1 text-center">
                    <img src={thenPhoto.filePath} alt="Then" className="w-full h-24 object-cover rounded-lg" />
                    <p className="text-[0.65rem] uppercase tracking-wide text-slate-400 mt-1">Then</p>
                  </div>
                  <span className="text-indigo-300 text-lg">→</span>
                  <div className="flex-1 text-center">
                    <img src={nowPhoto.filePath} alt="Now" className="w-full h-24 object-cover rounded-lg" />
                    <p className="text-[0.65rem] uppercase tracking-wide text-slate-400 mt-1">Now</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {isPublic && publicUrl ? (
          <div data-testid="milestone-share-url" className="rounded-xl p-4 bg-emerald-50 border border-emerald-100">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">Shareable link</p>
            <div className="flex flex-wrap items-center gap-2">
              <input readOnly value={publicUrl} className="w-full sm:w-auto sm:flex-1 min-w-0 text-sm border border-emerald-200 rounded-lg px-3 py-1.5 bg-white" />
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(publicUrl)}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700"
              >
                Copy
              </button>
              <button
                type="button"
                onClick={handleUnshare}
                className="px-3 py-1.5 bg-white text-emerald-700 rounded-lg text-xs font-semibold border border-emerald-200 hover:bg-emerald-100"
              >
                Unshare
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl p-4 bg-slate-50 border border-slate-100">
            <label className="block text-sm font-medium text-slate-700 mb-1">Link expires on (optional)</label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                type="date"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
                className="sm:flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="button"
                data-testid="milestone-share-button"
                onClick={handleShare}
                disabled={saving || !preview?.totalPhotos}
                className="px-4 py-2 bg-white border border-indigo-300 text-indigo-600 rounded-lg text-sm font-semibold hover:bg-indigo-50 disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                🔗 Get Shareable Link
              </button>
            </div>
          </div>
        )}

        {error && <p className="text-red-600 text-sm">{error}</p>}
        {savedMessage && <p className="text-emerald-600 text-sm">{savedMessage}</p>}

        <div className="flex flex-wrap gap-3 pt-1">
          <button
            type="button"
            data-testid="milestone-start-slideshow-button"
            onClick={handleStartSlideshow}
            disabled={saving || !preview?.totalPhotos}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            ▶ Start Slideshow
          </button>
          <button
            type="button"
            data-testid="milestone-save-button"
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 disabled:opacity-50 transition-colors"
          >
            💾 Save
          </button>
          <Link to="/events/milestones" className="px-6 py-2 text-slate-500 text-sm hover:text-slate-700 transition-colors">
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
}
